/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {colorMapAtom} from './diffServiceClient';
import {primerColorModeAtom} from './jotai/atoms';
import {useAtomValue} from 'jotai';
import React, {Component, Suspense, useEffect, useMemo} from 'react';
import {updateTextMateGrammarCSS} from 'shared/textmate-lib/textmateStyles';

/**
 * Silent error boundary for TextMate styles. Syntax highlighting is cosmetic,
 * so failures (e.g., WASM not loading in dev) should never crash the app.
 */
class TextMateErrorBoundary extends Component<
  {children: React.ReactNode},
  {hasError: boolean}
> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError() {
    return {hasError: true};
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.warn('TextMate syntax highlighting failed to initialize:', error.message);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

/**
 * Component that ensures TextMate syntax highlighting CSS is injected into
 * the DOM. It fetches the colorMap from the diff service worker and calls
 * updateTextMateGrammarCSS() to create CSS rules for the tokenization classes
 * (e.g., .mtk1, .mtk2, etc.).
 *
 * Wrapped in an error boundary so WASM/worker failures don't crash the app.
 */
// eslint-disable-next-line prefer-arrow-callback
export default React.memo(function TextMateStyles(): React.ReactElement | null {
  return (
    <TextMateErrorBoundary>
      <Suspense fallback={null}>
        <TextMateStylesInner />
      </Suspense>
    </TextMateErrorBoundary>
  );
});

function TextMateStylesInner(): React.ReactElement | null {
  const colorMode = useAtomValue(primerColorModeAtom);
  const colorAtom = useMemo(() => colorMapAtom(colorMode), [colorMode]);
  const colors = useAtomValue(colorAtom);

  useEffect(() => {
    updateTextMateGrammarCSS(colors);
  }, [colors]);

  return null;
}
